/** @module csi/slicer/_base */
define([
	"dojo/json",
	"dojo/store/Memory",
	"underscore",
	"csi",
	"csi/_base/Deferred",
	"csi/core/ExtendedPath",
	"csi/core/Key",
	"csi/core/SpecBuilder",
	"csi/def/Heading",
	"csi/locale",
	"csi/store/ExtractValues",
	"csi/xml",
	"csi/xml/Search"
], function(json, Memory, _, csi, Deferred, ExtendedPath, Key, SpecBuilder, Heading, locale, ExtractValues, xml, Search) {

	const WEIGHT_METRIC_SUFFIX = "_Weight";
	var _stores = {};

	/**
	 * Resolve classifiers and metrics against model
	 * @param {Node} extractStore 
	 * @param {Node[]} extractClassifiers
	 * @param {Node[]} extractMetrics
	 * @return {MetricObject}
	 * 
	 * @typedef {Object} MetricObject
	 * 	@property {string} id - metric Key
	 * 	@property {string} name - metric name
	 * 	@property {object} heading
	 * 	@property {string} func - metric expression (for custom metrics this field is formula representation
	 * 	@property {boolean}	count - if function is a count
	 * 	@property {string} total - metric key to calcualte total or ""
	 * 	@property {string} uniq - metric key to calculate unique
	 * 	@property {boolean} dynamic - dynamic metric (includes customs)
	 * 	@property {boolean} custom - custom metric
	 * 	@property {customPresentation|string} presentation - custom metric presentation object or dynamic (non custom) 
	 * 		metric aggregation type: "fnc" (same as ""), "sum", "min", "max", "avg"
	 * 
	 * @typedef {Object} customPresentation
	 * 	@property {string} name - custom metric name
	 * 	@property {string} subview - subview aka custom operation: 'sum','dif','prd','avg','mix','mrg','mrk','vrn'
	 * 	@property {string[]} [calcForNode] - array of metric keys
	 * 	@property {string} [metricANode] - metric key
	 * 	@property {string} [metricBNode] - metric key
	 * 	@property {string} [calcForSingleNode] - metric key
	 * 	@property {string} [costMetricNode] - metric key
	 * 	@property {string} [revenueMetricNode] - metric key
	 * 	@property {string} [typeSubTotalNode]: show sub/grand total raido button value
	 * 	@property {string} [total]: custom metric aggregation type
	 * 
	 */
	function _newStore(extractStore, extractClassifiers, extractMetrics) {
		const prefix = "ExtractStore_" + extractStore.$Name + "_";
		const type = extractStore.RowNodeType;
		const specBuilder = new SpecBuilder(type);

		function getSpec(node) {
			const format = node.Format.trim();
			const id = node.AttributeId;
			const name = getName(node);
			const extendedPath = ExtendedPath.create(type, node.AttributePath ?? "");
			const {standardPath} = extendedPath;
			const heading = Object.assign(new Heading(), {
				m_format: format,
				m_id: id,
				m_path: standardPath,
				m_name: name,
				m_value: node.Dynamic ? node.AggregateFunction : null
			});

			const key = new Key({
				extendedPath,
				id,
				name,
			});

			const params = {
				format,
				heading,
				name,
			};

			return specBuilder.getSpecForKey(key, params);
		}

		function getName(node) {
			const nodeName = node.$Name;
			const name = locale.resourceIfExists(prefix + nodeName);
			if (name) {
				return name;
			}

			if (nodeName == ExtractValues.METRIC_COUNT) {
				return locale.resource("Count");
			}

			if (node.Dynamic) {
				return nodeName;
			}
		}

		function getWeightAttributeName(node) {
			// Note attribute name weightAttributeId (not Attriubte Name here) as this refers to the name of weight attibute configured by user in Slicer Store.
			return node.WeightAttributeId ? node.$Name + WEIGHT_METRIC_SUFFIX : "";
		}

		// create stores for classifiers and metrics
		const classifiers = _.map(extractClassifiers, (node) => {
			const spec = getSpec(node);
			const {heading, name} = spec;

			// for now clear renderer classes since Slicer is not working with full nodes that renderers may require
			heading.classRenderer = null;

			return {
				id: node.$Name,
				heading,
				name,
				spec,
			};
		});

		// create list of weight metrics, then use this list to determine if given metric is weight metric or not (bWeight)
		const weightMetrics = [];
		extractMetrics.forEach((node) => {
			const weightAttributeName = getWeightAttributeName(node);
			if (weightAttributeName) {
				weightMetrics.push(weightAttributeName);
			}
		})

		const metrics = _.map(extractMetrics, (node) => {
			const bCount = (node.$Name == ExtractValues.METRIC_COUNT);
			const spec = getSpec(node);
			const {heading} = spec;

			heading.classRenderer = null;

			var uniq = node.AggregateFunction.match(/COUNT_UNIQUE\(.+?\)/g);
			if (uniq) {
				uniq = uniq[0].replace("COUNT_UNIQUE(", "").replace(")", "");
			};
			var total = node.AggregateFunction.match(/TOTAL\(.+?\)/g);
			if (total) {
				total = total[0].replace("TOTAL(", "").replace(")", "");
			}
			var bDynamic = node.Dynamic && !bCount;
			var presentationObj = {};
			var horizontalMix = null;
			if (node.Custom) {
				try {
					presentationObj = json.parse(node.Presentation);
					if (presentationObj.subview == "mix") {
						total = presentationObj.calcForSingleNode; // Id of the Metric, used in mix calculation
						var percentOf = presentationObj.typePercentOfNode;
						if ((percentOf == "rowsub") || (percentOf == "rowgrand") || (percentOf == "totalgrand")) {
							// Horizontal aggregation case for %mix calculation
							horizontalMix = percentOf;
						}
					}
				} catch (ex) {
					console.error("Failed to parse custom metric presentation", node.$Name, node.Presentation);
				}
			} else if (bDynamic) {
				presentationObj = node.Presentation;
			}

			const bWeight = weightMetrics.indexOf(node.$Name) >= 0;
			return {
				id: node.$Name,
				name: presentationObj.name || spec.name,
				heading,
				func: bCount ? "SUM" : node.AggregateFunction,
				count: bCount,
				total,
				horizontalMix,
				uniq,
				dynamic: bDynamic && !bWeight,
				custom: node.Custom,
				presentation: presentationObj,
				spec,
				weight: bWeight,
				// Note attribute name weightAttributeName (not Attribute Id here) as this refers to the name of weight attibute, created by server
				weightAttributeName: getWeightAttributeName(node),
				//weightAttributeIdx: null // column index of weight attribute (to be calculated later inside parseCube function )
			};
		});

		return _stores[extractStore.$Name] = {
			classifiers: new Memory({data: csi.sort(classifiers, "name")}),
			metrics: new Memory({data: csi.sort(metrics, "name")}),
		};
	}

	return {
		WEIGHT_METRIC_SUFFIX,

		/**
		 * Query server for store definition returning Deferred that resolves to SlicerStore
		 * @param {String} storeName
		 * @param {Boolean} bForceRefresh
		 * @return {Deferred} 
		 */
		getStore: function(storeName, bForceRefresh) {
			var dfd = new Deferred();

			if (_stores[storeName] && !bForceRefresh) {
				dfd.resolve(_stores[storeName]);
			} else {
				var search = new Search();
				search.addParameter("Dep.Path", "Child:Classifiers;Metrics");
				search.addParameter("Fmt.Attr.Info", "Min");
				search.addParameter("Fmt.Node.Info", "Min");
				search.getQuery()
					.Node(xml.PROP_TYPE, xml.OP_EQUALS, "ExtractStore")
					.Node(xml.PROP_NAME, xml.OP_EQUALS, storeName);

				search.execute().then(
					function(res) {
						var extractStore = res.getResultNode();
						if (extractStore) {
							dfd.resolve(_newStore(extractStore, res.getNodesOfType("ExtractClassifier"), res.getNodesOfType("ExtractMetric")));
						} else {
							const errMsg = locale.resource("SLICER_STORE_NOT_FOUND", [storeName]);
							console.error(errMsg);
							dfd.reject(new Error(errMsg));
						}
					},
					function(err) {
						dfd.reject(err);
					}
				);
			}

			return dfd;
		}
	};
});